import { inject, Injectable } from '@angular/core';
import {
    Industry,
    MatchmakingFormConfig,
    OptionItem,
    palletizingQuestions
} from 'matchmaking/models';
import {
    INVESTMENT_GOALS,
    INVESTMENT_LEVELS,
    INVESTMENT_TIMELINE
} from 'matchmaking/constants';
import { MatchmakingDataService, MatchmakingTranslateService } from 'matchmaking/services';

@Injectable({
    providedIn: 'root',
})
export class FormularMetaDataService {
    translateService = inject(MatchmakingTranslateService);
    dataService = inject(MatchmakingDataService);

    buildReadableFormMetadata(
        config: MatchmakingFormConfig,
        applicationAsProperty: string
    ): string {
        if (!config) {
            return '';
        }

        let applicationSpecificData: string[] = [];

        if (config.stepApplicationSpecific.customPage) {
            const data = config.stepApplicationSpecific.questions;

            applicationSpecificData = [
                `${applicationAsProperty}_shape:${data.question1}`,
                `${applicationAsProperty}_weight:${data.question2}`,
                `${applicationAsProperty}_speed:${data.question3}`,
                `${applicationAsProperty}_support:${data.question4}`,
                `${applicationAsProperty}_integration:${data.question5}`,
                `${applicationAsProperty}_other:${data.question6}`,
            ];
        } else {
            applicationSpecificData = [
                `${applicationAsProperty}_other:${config.stepApplicationSpecific.otherText}`,
            ];
        }

        const metadata = [
            `budgetRange:${config.budgetRange}`,
            `decisionMaker:${config.decisionMaker}`,
            `projectGoal:${config.projectGoal}`,
            `application:${config.selectedApplication}`,
            `industry:${this.getIndustryValue(config)}`,
            `timeline:${config?.timeline}`,
        ];

        return [...metadata, ...applicationSpecificData].join(';') + ';';
    }

    buildAnalyticsMetaData(
        industryId: number,
        applicationId: number,
        config: MatchmakingFormConfig
    ): string {
        if (!config) {
            return '';
        }

        const metadata = [
            `industry:${industryId}`,
            `application:${applicationId}`,
            `projectGoal:${this.getIdByTitle(INVESTMENT_GOALS, config.projectGoal || '')}`,
            `budgetRange:${this.getIdByTitle(INVESTMENT_LEVELS, config.budgetRange || '')}`,
            `decisionMaker:${config.decisionMaker}`,
            `timeline:${this.getIdByTitle(INVESTMENT_TIMELINE, config?.timeline || '')}`,
        ];

        if (config.stepApplicationSpecific.customPage) {
            const data = config.stepApplicationSpecific.questions;
            const applicationSpecificData = `
                application_specific:
                    ${this.getIdByTitle(palletizingQuestions.question1, data.question1)},
                    ${this.getIdByTitle(palletizingQuestions.question2, data.question2)},
                    ${this.getIdByTitle(palletizingQuestions.question3, data.question3)},
                    ${this.getIdByTitle(palletizingQuestions.question4, data.question4)},
                    ${this.getIdByTitle(palletizingQuestions.question5, data.question5)},`;

            metadata.push(applicationSpecificData);
        }

        return metadata.join(';') + ';';
    }


    getIndustryValue(config: MatchmakingFormConfig): string {
        const industry = this.dataService.getSelectedIndustry();

        return industry?.externalId === Industry.Other
            ? `${industry.title} - ${config.customIndustryText?.trim() || ''}`
            : industry?.title || '';
    }

    // Both of these need to return the translation value, but here they only return the key
    getTimelineSalesforceValue(timeline: string): string {
        const item = INVESTMENT_TIMELINE.find((item) => this.translateService.getTranslation(item.title) === timeline);
        return item?.salesforceMappingValue || '';
    }

    private getIdByTitle(options: OptionItem[], title: string): number {
        const item = options.find((item) => this.translateService.getTranslation(item.title) === title);
        return item?.id || 0;
    }
}
